import { Button, Form, Input, Select, Modal, Space } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { REQUEST_STATUSES } from 'commons/types/common';
import { signoutAsync } from 'stores/auth/authSlice';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import { useUser } from 'stores/user/userHooks';
import {
  getUserUpdateRequestStatus,
  requestPhoneOtpAsync,
  updateUserAsync,
} from 'stores/user/userSlice';
import { User } from 'stores/user/userTypes';
import UpdateEmail from 'components/modals/UpdateEmail';
import UpdatePhone from 'components/modals/UpdatePhone';
import { CheckCircleFilled } from '@ant-design/icons';

export default function Profile() {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [user] = useUser();
  const [changePhoneModal, setChangePhoneModal] = useState(false);
  const [changeEmailModal, setChangeEmailModal] = useState(false);
  const [phoneStep, setPhoneStep] = useState('phone');
  const [newPhone, setNewPhone] = useState('');
  const userUpdateRequestStatus = useAppSelector(getUserUpdateRequestStatus);

  const onSignOut = useCallback(() => dispatch(signoutAsync()), [dispatch]);
  const onFinish = useCallback(
    (newUser: User) => {
      dispatch(
        updateUserAsync({
          id: user?.id,
          ...newUser,
        }),
      );
    },
    [dispatch, user?.id],
  );

  const openEmailModal = useCallback(() => {
    setChangeEmailModal(true);
  }, []);

  const closeEmailModal = useCallback(() => {
    setChangeEmailModal(false);
  }, []);

  const openPhoneModal = useCallback(() => {
    setChangePhoneModal(true);
  }, []);

  const validateCurrentPhone = useCallback(() => {
    if (!user?.phone) {
      return;
    }
    setNewPhone(user.phone);
    dispatch(requestPhoneOtpAsync({ phone: user?.phone }));
    setPhoneStep('code');
    setChangePhoneModal(true);
  }, [dispatch, user?.phone]);

  const closePhoneModal = useCallback(() => {
    setChangePhoneModal(false);
  }, []);

  useEffect(() => {
    form.setFieldsValue(user);
  }, [form, user]);

  return (
    <div className='flex flex-col px-4'>
      <Form
        form={form}
        layout='vertical'
        name='profile-form'
        onFinish={onFinish}
      >
        <Form.Item
          label='Prénom'
          name='firstName'
          rules={[{ required: true, type: 'string' }]}
        >
          <Input
            size='large'
            className='!rounded-2xl font-semibold'
          />
        </Form.Item>
        <Form.Item
          label='Nom'
          name='lastName'
          rules={[{ required: true, type: 'string' }]}
        >
          <Input
            size='large'
            className='!rounded-2xl font-semibold'
          />
        </Form.Item>
        <Form.Item
          label='Email'
          name='email'
          rules={[{ required: true, type: 'email' }]}
        >
          <Space.Compact className='w-full'>
            <Input
              size='large'
              className='!rounded-l-xl font-semibold'
              value={user?.email}
              disabled
            />
            <Button
              type='primary'
              className='!rounded-r-xl'
              size='large'
              onClick={openEmailModal}
            >
              modifier
            </Button>
          </Space.Compact>
        </Form.Item>
        <Form.Item
          label='Téléphone'
          name='phone'
          rules={[{ required: true, type: 'string' }]}
        >
          <Space.Compact className='w-full'>
            <Input
              size='large'
              className='!rounded-l-xl font-semibold'
              value={user?.phone}
              disabled
            />
            <Button
              type='primary'
              size='large'
              className='!rounded-r-xl'
              onClick={openPhoneModal}
            >
              modifier
            </Button>
          </Space.Compact>
          {user?.phone_validated ? (
            <div className='flex flex-row text-green-600'>
              <CheckCircleFilled />
              <p className='m-2'>Validé</p>
            </div>
          ) : (
            <div className='flex flex-row text-red-400'>
              <button
                className='m-2'
                onClick={validateCurrentPhone}
              >
                Je valide mon numéro
              </button>
            </div>
          )}
        </Form.Item>
        <Form.Item
          label='Adresse'
          name={['patient', 'address']}
          rules={[{ required: false, type: 'string' }]}
        >
          <Input
            size='large'
            className='!rounded-2xl font-semibold'
          />
        </Form.Item>
        <Form.Item
          label='Code postal'
          name={['patient', 'zip']}
          rules={[{ required: true, type: 'string' }]}
        >
          <Input
            size='large'
            className='!rounded-2xl font-semibold'
          />
        </Form.Item>
        <Form.Item
          label='Ville'
          name={['patient', 'city']}
          rules={[{ required: false, type: 'string' }]}
        >
          <Input
            size='large'
            className='!rounded-2xl font-semibold'
          />
        </Form.Item>
        <Form.Item
          label='Date de naissance'
          name='birthday'
          rules={[{ required: false, type: 'string' }]}
        >
          <Input
            size='large'
            className='!rounded-2xl font-semibold'
          />
        </Form.Item>
        <Form.Item
          label='Sexe'
          name='sexe'
          rules={[{ required: false, type: 'string' }]}
        >
          <Select
            size='large'
            className='!rounded-2xl !font-semibold'
            options={[
              { value: 'male', label: 'Homme' },
              { value: 'female', label: 'Femme' },
              { value: 'other', label: 'Autre' },
            ]}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type='primary'
            className='!rounded-3xl !h-12 !font-semibold mt-2 w-full'
            htmlType='submit'
            loading={userUpdateRequestStatus === REQUEST_STATUSES.PENDING}
          >
            Sauvegarder
          </Button>
        </Form.Item>
      </Form>
      <Button
        type='link'
        danger
        className='!font-semibold w-full'
        onClick={onSignOut}
      >
        Je me déconnecte
      </Button>
      <Modal
        className='roundedModal p-20'
        open={changeEmailModal}
        onCancel={closeEmailModal}
        footer={null}
      >
        <UpdateEmail />
      </Modal>
      <Modal
        className='roundedModal p-20'
        open={changePhoneModal}
        onCancel={closePhoneModal}
        footer={null}
      >
        <UpdatePhone
          step={phoneStep}
          setStep={setPhoneStep}
          newPhone={newPhone}
          setNewPhone={setNewPhone}
        />
      </Modal>
    </div>
  );
}
